import NoAccessPage from "@entrilia/shared/components/NoAccessPage";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router";
import { redirectToSignin } from "./auth/authRedirects";
import GenericObjectDetailsPage from "./components/entityFields/grid/GenericObjectDetailsPage";
import NewObjectDetailsPage from "./components/entityFields/grid/NewObjectDetailsPage";
import RecordsPage from "./components/entityFields/grid/RecordsPage";
import AccessCategoriesPage from "./components/pages/access-categories/AccessCategoriesPage";
import AgenticWorkflowsPage from "./components/pages/agentic-workflows/AgenticWorkflowsPage";
import AgentInboxSettingsPage from "./components/pages/agentic-workflows/agent-inboxes/AgentInboxSettingsPage";
import ApiGatewayPage from "./components/pages/api-gateway/ApiGatewayPage";
import ApprovalObjectPage from "./components/pages/approvals/ApprovalObjectPage";
import ApprovalsPage from "./components/pages/approvals/ApprovalsPage";
import AssistantPage from "./components/pages/assistant/AssistantPage";
import AssistantTaskPage from "./components/pages/assistant/tasks/AssistantTaskPage";
import BusinessCentralRedirectPage from "./components/pages/bc-redirect/BusinessCentralRedirectPage";
import ChangeRequestsPage from "./components/pages/change-requests/ChangeRequestsPage";
import ChangeRequestDetailsPage from "./components/pages/change-requests/details/ChangeRequestDetailsPage";
import ChecklistsPage from "./components/pages/checklists/ChecklistsPage";
import DealCloudConnectorPage from "./components/pages/crm-connector/DealCloudConnectorPage";
import SalesforceConnectorPage from "./components/pages/crm-connector/SalesforceConnectorPage";
import CompanyDashboardsPage from "./components/pages/dashboard/CompanyDashboardsPage";
import DashboardPage from "./components/pages/dashboard/DashboardPage";
import DashboardPageEdit from "./components/pages/dashboard/DashboardPageEdit";
import DashboardPageView from "./components/pages/dashboard/DashboardPageView";
import TileViewOverlay from "./components/pages/dashboard/widgets/TileViewOverlay";
import {
  SmartViewWidgetReportEditor,
  SmartViewWidgetReportEditorApp,
} from "./components/pages/dashboard/widgets/smartview/SmartViewWidgetReportEditor";
import { DataBackupPage } from "./components/pages/data-backup/DataBackupPage";
import DataRequestSetupPage from "./components/pages/data-collections/data-request-templates/DataRequestSetupPage";
import DataRequestTemplatePage from "./components/pages/data-collections/data-request-templates/DataRequestTemplatePage";
import DataRequestsPage from "./components/pages/data-collections/data-requests/DataRequestsPage";
import DataRequestPage from "./components/pages/data-collections/data-requests/data-request-details/DataRequestPage";
import DataSubmissionsPage from "./components/pages/data-collections/data-submissions/DataSubmissionsPage";
import DataSubmissionPage from "./components/pages/data-collections/data-submissions/data-submission-details/DataSubmissionPage";
import DataModelPage from "./components/pages/data-model/DataModelPage";
import ObjectDefinitionWrapperPage from "./components/pages/data-model/ObjectDefinitionWrapperPage";
import ChecklistsConfigurationPage from "./components/pages/data-model/checklists/ChecklistsConfigurationPage";
import ObjectLayoutPage from "./components/pages/data-model/layouts/ObjectLayoutPage";
import ObjectDetailsPage from "./components/pages/data-model/object-details/ObjectDetailsPage";
import SnowflakeConnectorPage from "./components/pages/dwh-connector/SnowflakeConnectorPage";
import InvestorEmailDeliveryLogPage from "./components/pages/email-delivery-log/InvestorEmailDeliveryLogPage";
import PortfolioEmailDeliveryLogPage from "./components/pages/email-delivery-log/PortfolioEmailDeliveryLogPage";
import EntriliaMcpPage from "./components/pages/entrilia-mcp/EntriliaMcpPage";
import InvoiceDetailsPage from "./components/pages/expense-management/invoices/InvoiceDetailsPage";
import InvoicesPage from "./components/pages/expense-management/invoices/InvoicesPage";
import VendorDetailsPage from "./components/pages/expense-management/vendors/VendorDetailsPage";
import VendorsPage from "./components/pages/expense-management/vendors/VendorsPage";
import CompanyFilesPage from "./components/pages/files/CompanyFilesPage";
import FundOpsTransactionLinkPage from "./components/pages/fund-ops-link/FundOpsTransactionLinkPage";
import FundStructureContactPage from "./components/pages/fund-structure/contacts/FundStructureContactPage";
import FundStructureContactsPage from "./components/pages/fund-structure/contacts/FundStructureContactsPage";
import ContactDocumentActivityPage from "./components/pages/fund-structure/contacts/documents/ContactDocumentActivityPage";
import ContactFundraisingDocumentActivityPage from "./components/pages/fund-structure/contacts/fundraising-documents/ContactFundraisingDocumentActivityPage";
import FundStructureFundPage from "./components/pages/fund-structure/funds/FundStructureFundPage";
import FundStructureFundsPage from "./components/pages/fund-structure/funds/FundStructureFundsPage";
import FundStructureInvestorPage from "./components/pages/fund-structure/investors/FundStructureInvestorPage";
import FundStructureInvestorsPage from "./components/pages/fund-structure/investors/FundStructureInvestorsPage";
import InvestorDocumentActivityPage from "./components/pages/fund-structure/investors/documents/InvestorDocumentActivityPage";
import InvestorFundraisingDocumentActivityPage from "./components/pages/fund-structure/investors/fundraising-documents/InvestorFundraisingDocumentActivityPage";
import HomePage from "./components/pages/home/HomePage";
import ImportDataPage from "./components/pages/import-data/ImportDataPage";
import ImportDataReviewPage from "./components/pages/import-data/ImportDataReviewPage";
import ImportCsvDataPage from "./components/pages/import-data/csv-import/ImportCsvDataPage";
import ImportDataDetailsPage from "./components/pages/import-data/details/ImportDataDetailsPage";
import DocumentCollectionsPage from "./components/pages/insights/DocumentCollectionsPage";
import DocumentCollectionPage from "./components/pages/insights/document-collection/DocumentCollectionPage";
import InvestorCommunicationsPage from "./components/pages/investor-communications/InvestorCommunicationsPage";
import InvestorCommunicationDetailsPage from "./components/pages/investor-communications/communication-details/InvestorCommunicationDetailsPage";
import UserConsentDetailsPage from "./components/pages/investor-portal/consents/UserConsentDetailsPage";
import DocumentActivityPage from "./components/pages/investor-portal/document-activity/DocumentActivityPage";
import FundraisingsPage from "./components/pages/investor-portal/fundraising/FundraisingsPage";
import FundraisingDetailsPage from "./components/pages/investor-portal/fundraising/fundraising-details/FundraisingDetailsPage";
import InvestorPortalPromoPage from "./components/pages/investor-portal/promo/InvestorPortalPromoPage";
import InvestorPortalSettingsPage from "./components/pages/investor-portal/settings/InvestorPortalSettingsPage";
import DashboardWidgetSettingsPage from "./components/pages/investor-portal/widgets/DashboardWidgetSettingsPage";
import PortfolioWidgetSettingsPage from "./components/pages/investor-portal/widgets/PortfolioWidgetSettingsPage";
import PassthroughPage from "./components/pages/passthrough/PassthroughPage";
import FundInvestmentDetailsPage from "./components/pages/portfolio-monitoring/fund-investments/FundInvestmentDetailsPage";
import FundInvestmentsPage from "./components/pages/portfolio-monitoring/fund-investments/FundInvestmentsPage";
import MetricsPage from "./components/pages/portfolio-monitoring/metrics/MetricsPage";
import PortfolioCompaniesPage from "./components/pages/portfolio-monitoring/portfolio-companies/PortfolioCompaniesPage";
import PortfolioCompanyDetailsPage from "./components/pages/portfolio-monitoring/portfolio-companies/PortfolioCompanyDetailsPage";
import ScenarioDetailsPage from "./components/pages/portfolio-monitoring/scenarios/ScenarioDetailsPage";
import ScenariosPage from "./components/pages/portfolio-monitoring/scenarios/ScenariosPage";
import PortfolioPortalPage from "./components/pages/portfolio-portal/PortfolioPortalPage";
import ArtifactPage from "./components/pages/reporting/artifacts/ArtifactPage";
import ArtifactsListPage from "./components/pages/reporting/artifacts/ArtifactsListPage";
import ArtifactReportContainer from "./components/pages/reporting/artifacts/report/ArtifactReportContainer";
import ArtifactReportWindow from "./components/pages/reporting/artifacts/report/ArtifactReportWindow";
import CompanyApiReportsPage from "./components/pages/reporting/report-lists/CompanyApiReportsPage";
import CompanySystemReportsPage from "./components/pages/reporting/report-lists/CompanySystemReportsPage";
import CompanyUiReportsPage from "./components/pages/reporting/report-lists/CompanyUiReportsPage";
import OrganizationTemplatesPage from "./components/pages/reporting/report-lists/OrganizationTemplatesPage";
import GroupsPage from "./components/pages/reporting/report-lists/groups/GroupsPage";
import CompanyApiReportsContainer from "./components/pages/reporting/report-lists/reports/api-reports/CompanyApiReportsContainer";
import CompanySystemReportsContainer from "./components/pages/reporting/report-lists/reports/system-reports/CompanySystemReportsContainer";
import CompanyUiReportsContainer from "./components/pages/reporting/report-lists/reports/ui-reports/CompanyUiReportsContainer";
import OrganizationTemplatesContainer from "./components/pages/reporting/report-lists/templates/OrganizationTemplatesContainer";
import StandardReportsPage from "./components/pages/reporting/standard-reports/StandardReportsPage";
import {
  ApAgingDetailReportPage,
  ArAgingDetailReportPage,
} from "./components/pages/reporting/standard-reports/ap-ar-aging-detail/ApArAgingDetailReportPage";
import {
  ApAgingSummaryReportPage,
  ArAgingSummaryReportPage,
} from "./components/pages/reporting/standard-reports/ap-ar-aging-summary/ApArAgingSummaryReportPage";
import BalanceSheetReport, {
  IncomeStatementReport,
} from "./components/pages/reporting/standard-reports/balance-sheet/BalanceSheetPage";
import SmartViewApiReportPage from "./components/pages/reporting/sv/SmartViewApiReportPage";
import SmartViewSystemReportPage from "./components/pages/reporting/sv/SmartViewSystemReportPage";
import SmartViewTemplatePage from "./components/pages/reporting/sv/SmartViewTemplatePage";
import SmartViewUiReportPage from "./components/pages/reporting/sv/SmartViewUiReportPage";
import CreateDraftFromConfigurationPage from "./components/pages/reporting/sv/components/CreateDraftFromConfigurationPage";
import { EditRoot as SmartViewEditRoot } from "./components/pages/reporting/sv/components/builder/EditRoot";
import SmartViewViewRoot from "./components/pages/reporting/sv/components/builder/ViewRoot";
import ActivityLogPage from "./components/pages/settings/activity-log/ActivityLogPage";
import GeneralCompanySettingsPage from "./components/pages/settings/company/GeneralCompanySettingsPage";
import IntegrationsPage from "./components/pages/settings/integrations/IntegrationsPage";
import MembersSettingsPage from "./components/pages/settings/members/MembersSettingsPage";
import OrganizationContactsPage from "./components/pages/settings/organization-contacts/OrganizationContactsPage";
import OrganizationReportsPage from "./components/pages/settings/organization-reports/OrganizationReportsPage";
import ApiOrganizationReportsPage from "./components/pages/settings/organization-reports/api-reports/ApiOrganizationReportsPage";
import UiOrganizationReportsPage from "./components/pages/settings/organization-reports/ui-reports/UiOrganizationReportsPage";
import NewUserGroupPage from "./components/pages/settings/user-groups/NewUserGroupPage";
import UserGroupDetailsPage from "./components/pages/settings/user-groups/UserGroupDetailsPage";
import UserGroupsPage from "./components/pages/settings/user-groups/UserGroupsPage";
import NewChecklistTaskTemplatePage from "./components/pages/task-templates/NewChecklistTaskTemplatePage";
import TaskTemplateDetailsPage from "./components/pages/task-templates/TaskTemplateDetailsPage";
import AccountSettingsPage from "./components/pages/user-settings/AccountSettingsPage";
import VideoLibraryPage from "./components/pages/video-library/VideoLibraryPage";
import VideoPage from "./components/pages/video-library/VideoPage";
import WorkbookRunnerContainer from "./components/pages/workbook-runner/WorkbookRunnerContainer";
import WorkbookRunnerPage from "./components/pages/workbook-runner/WorkbookRunnerPage";
import WorkbookRunDetailsPage from "./components/pages/workbook-runner/runs/details/WorkbookRunDetailsPage";
import WorkbookTemplateDetailsPage from "./components/pages/workbook-runner/templates/details/WorkbookTemplateDetailsPage";
import ApprovalConfigurationPage from "./components/pages/workflows/approval-config/ApprovalConfigurationPage";
import WorkflowChecklistDetailsPage from "./components/pages/workflows/checklists/WorkflowChecklistDetailsPage";
import WorkflowGroupsPage from "./components/pages/workflows/workflow-groups/WorkflowGroupsPage";
import WorkflowRunsPage from "./components/pages/workflows/workflow-runs/WorkflowRunsPage";
import WorkflowRunDetailsPage from "./components/pages/workflows/workflow-runs/details/WorkflowRunDetailsPage";
import WorkflowTemplateActivityFieldsPage from "./components/pages/workflows/workflow-templates/WorkflowTemplateActivityFieldsPage";
import WorkflowTemplateDetailsPage from "./components/pages/workflows/workflow-templates/WorkflowTemplateDetailsPage";
import WorkflowTemplatesPage from "./components/pages/workflows/workflow-templates/WorkflowTemplatesPage";
import useClientContext from "./context/useClientContext";
import useUserContext from "./context/useUserContext";
import useInvestorRelationsAccess from "./hooks/useInvestorRelationsAccess";
import { pageRoutes } from "./routes";

const NoAccess = () => {
  const { email: userEmail } = useUserContext();
  return <NoAccessPage userEmail={userEmail} onSignInWithAnotherAccountClick={redirectToSignin} />;
};

const artifactReportRoutes = (
  <Route element={<ArtifactReportWindow />}>
    <Route element={<ArtifactReportContainer />}>
      <Route path="report/edit" element={<SmartViewEditRoot />} />
      <Route path="report/view" element={<SmartViewViewRoot />} />
    </Route>
  </Route>
);

const Pages = () => {
  const { client } = useParams();

  const {
    clientCode,
    hasAnyPermission,
    isFundAdmin,
    isPortalFeatureEnabled,
    isAllowedToAccessAllEntities,
    investorPortalApp,
    reportingPortalApp,
  } = useClientContext();

  const {
    hasAccessToFundStructure,
    hasAccessToInvestorEmails,
    hasAccessToInvestorPortalSettings,
    hasAccessToInvestorRequests,
    hasAccessToDocumentCollections,
    hasAccessToDocumentActivity,
    hasAccessToFundraising,
    hasAccessToInvestorCommunications,
    hasAccessToInvestorRelations,
  } = useInvestorRelationsAccess();

  const hasAccessToOrganizationUsers =
    hasAnyPermission(["ManageOrganizationUsers", "ViewOrganizationUsers"]) && isFundAdmin;

  const hasAccessToCompanySettings = hasAnyPermission(["ViewCompanySettings", "ManageCompanySettings"]);

  const hasAccessToIntegrationSettings = hasAnyPermission(["ViewClientIntegrations", "ManageClientIntegrations"]);

  const hasAccessToAssistant = hasAnyPermission(["UseAiAssistant"]) && isPortalFeatureEnabled("AiAssistantEnabled");

  const hasAccessToOrganizationReports = hasAnyPermission(["ManageOrganizationReports"]) && isFundAdmin;

  const hasAccessToReports = hasAnyPermission(["ManageReports", "ViewReports"]);

  const hasAccessToCompanyFiles =
    hasAnyPermission(["ViewAllCompanyFiles", "ManageAllCompanyFiles"]) && isPortalFeatureEnabled("EntityFilesEnabled");

  const hasAccessToExpenseManagement = hasAnyPermission(["ViewExpenses", "ManageExpenses"]);

  const hasAccessToPortfolioMonitoring = hasAnyPermission([
    "ViewPortfolioMonitoring",
    "ManagePortfolioMonitoringAssignedDataSubmissions",
    "ManagePortfolioMonitoring",
  ]);

  const hasAccessToImportData =
    isAllowedToAccessAllEntities &&
    hasAnyPermission([
      "ViewInvestorRelationsDataImports",
      "ManageInvestorRelationsDataImports",
      "ViewPortfolioMonitoringDataImports",
      "ManagePortfolioMonitoringDataImports",
      "ViewExpenseManagementDataImports",
      "ManageExpenseManagementDataImports",
    ]);

  const hasAccessToWorkflows = hasAnyPermission(["ViewWorkflows", "ManageWorkflows"]);

  const hasAccessToOrganizationDataModel =
    hasAnyPermission(["ViewOrganizationDataModel", "ManageOrganizationDataModel"]) && isFundAdmin;

  const hasAccessToDashboards = hasAnyPermission(["ViewDashboards", "ManageDashboards"]);
  const hasAccessToAiDashboards =
    isPortalFeatureEnabled("AiDashboardsEnabled") &&
    hasAccessToDashboards &&
    hasAccessToReports &&
    !isFundAdmin &&
    !!reportingPortalApp;

  const hasAccessToAgentInboxes = hasAnyPermission(["ViewInboxes", "ManageInboxes"]);

  const hasAccessToActivityLog = hasAnyPermission(["ViewActivityLog"]);

  const hasAccessToFundOpsLink = hasAnyPermission(["ViewFundOpsLink"]);

  return (
    <Routes>
      <Route path="/" element={<Navigate replace to={`/${client}/${pageRoutes.home}`} />} />

      <Route path={pageRoutes.home} element={<HomePage />} />

      {isPortalFeatureEnabled("AiAssistantEnabled") && (
        <Route path={pageRoutes.assistant} element={hasAccessToAssistant ? <Outlet /> : <NoAccess />}>
          <Route path=":threadId?" element={<AssistantPage />} />
          <Route path="tasks/:taskId" element={<AssistantTaskPage />} />
          <Route path="tasks" element={<Navigate replace to={`/${client}/${pageRoutes.assistant}`} />} />
        </Route>
      )}

      <Route path={pageRoutes.investorRelations} element={hasAccessToInvestorRelations ? <Outlet /> : <NoAccess />}>
        <Route
          path={pageRoutes.investorPortalSettings}
          element={hasAccessToInvestorPortalSettings && investorPortalApp ? <Outlet /> : <NoAccess />}
        >
          <Route index element={<InvestorPortalSettingsPage />} />
          <Route path={pageRoutes.dashboardWidgetSettings} element={<DashboardWidgetSettingsPage />} />
          <Route path={pageRoutes.portfolioWidgetSettings} element={<PortfolioWidgetSettingsPage />} />
        </Route>
        <Route
          path={pageRoutes.userConsents}
          element={hasAccessToInvestorPortalSettings && investorPortalApp ? <Outlet /> : <NoAccess />}
        >
          <Route path=":name" element={<UserConsentDetailsPage />} />
        </Route>
        <Route
          path={pageRoutes.investorEmails}
          element={hasAccessToInvestorEmails ? <InvestorEmailDeliveryLogPage /> : <NoAccess />}
        />
        <Route
          path={pageRoutes.accessCategories}
          element={hasAccessToInvestorPortalSettings ? <AccessCategoriesPage /> : <NoAccess />}
        />
        <Route
          path={pageRoutes.investorCommunications}
          element={hasAccessToInvestorCommunications ? <Outlet /> : <NoAccess />}
        >
          <Route index element={<InvestorCommunicationsPage />} />
          <Route path=":id" element={<InvestorCommunicationDetailsPage />} />
        </Route>
        <Route
          path={pageRoutes.documentCollections}
          element={hasAccessToDocumentCollections ? <Outlet /> : <NoAccess />}
        >
          <Route index element={<DocumentCollectionsPage />} />
          <Route path=":id" element={<DocumentCollectionPage />} />
        </Route>
        <Route path={pageRoutes.fundraisings} element={hasAccessToFundraising ? <Outlet /> : <NoAccess />}>
          <Route index element={<FundraisingsPage />} />
          <Route path=":id" element={<FundraisingDetailsPage />} />
        </Route>
        <Route
          path={pageRoutes.documentActivity}
          element={hasAccessToDocumentActivity && investorPortalApp ? <Outlet /> : <NoAccess />}
        >
          <Route index element={<DocumentActivityPage />} />
        </Route>
        <Route path={pageRoutes.funds} element={hasAccessToFundStructure ? <Outlet /> : <NoAccess />}>
          <Route index element={<FundStructureFundsPage />} />
          <Route path=":id/:tab" element={<FundStructureFundPage />} />
        </Route>
        <Route path={pageRoutes.investors} element={hasAccessToFundStructure ? <Outlet /> : <NoAccess />}>
          <Route index element={<FundStructureInvestorsPage />} />
          <Route path=":id/:tab" element={<FundStructureInvestorPage />} />
          <Route
            path={`:id/${pageRoutes.messages}/:postMessageRequestId/${pageRoutes.documents}/:attachmentId/${pageRoutes.activity}`}
            element={hasAccessToDocumentActivity ? <InvestorDocumentActivityPage /> : <NoAccess />}
          />
          <Route
            path={`:id/${pageRoutes.fundraisings}/:fundraisingId/${pageRoutes.documents}/:documentId/${pageRoutes.activity}`}
            element={hasAccessToDocumentActivity ? <InvestorFundraisingDocumentActivityPage /> : <NoAccess />}
          />
        </Route>
        <Route path={pageRoutes.contacts} element={hasAccessToFundStructure ? <Outlet /> : <NoAccess />}>
          <Route index element={<FundStructureContactsPage />} />
          <Route path=":id/:tab" element={<FundStructureContactPage />} />
          <Route
            path={`:id/${pageRoutes.messages}/:postMessageRequestId/${pageRoutes.documents}/:attachmentId/${pageRoutes.activity}`}
            element={hasAccessToDocumentActivity ? <ContactDocumentActivityPage /> : <NoAccess />}
          />
          <Route
            path={`:id/${pageRoutes.fundraisings}/:fundraisingId/${pageRoutes.documents}/:documentId/${pageRoutes.activity}`}
            element={hasAccessToDocumentActivity ? <ContactFundraisingDocumentActivityPage /> : <NoAccess />}
          />
        </Route>
        <Route
          path={pageRoutes.requests}
          element={hasAccessToInvestorRequests && investorPortalApp ? <Outlet /> : <NoAccess />}
        >
          <Route index element={<ChangeRequestsPage />} />
          <Route path=":id" element={<ChangeRequestDetailsPage backButtonPath=".." />} />
        </Route>
        {!investorPortalApp && <Route path={pageRoutes.aboutInvestorPortal} element={<InvestorPortalPromoPage />} />}
      </Route>

      <Route path={pageRoutes.smartViews} element={<Outlet />}>
        <Route path={pageRoutes.companyReports} element={hasAccessToReports ? <CompanyUiReportsPage /> : <NoAccess />}>
          <Route index element={<CompanyUiReportsContainer />} />
          <Route
            path={pageRoutes.reportLevels}
            element={<GroupsPage permissions={["ManageAllReports"]} title="Report Groups" type="report" />}
          />
        </Route>
        <Route path={pageRoutes.apiReports} element={hasAccessToReports ? <CompanyApiReportsPage /> : <NoAccess />}>
          <Route index element={<CompanyApiReportsContainer />} />
          <Route
            path={pageRoutes.reportLevels}
            element={<GroupsPage permissions={["ManageAllReports"]} title="Report Groups" type="report" />}
          />
        </Route>
        <Route
          path={pageRoutes.systemReports}
          element={hasAccessToReports ? <CompanySystemReportsPage /> : <NoAccess />}
        >
          <Route index element={<CompanySystemReportsContainer />} />
          <Route
            path={pageRoutes.reportLevels}
            element={<GroupsPage permissions={["ManageAllReports"]} title="Report Groups" type="report" />}
          />
        </Route>
        <Route
          path={`${pageRoutes.companyReports}/*`}
          element={
            hasAccessToReports ? (
              <Suspense fallback={<InlineLoader />}>
                <SmartViewUiReportPage />
              </Suspense>
            ) : (
              <NoAccess />
            )
          }
        />
        <Route
          path={`${pageRoutes.companyReportConfiguration}/:configurationId`}
          element={hasAccessToReports ? <CreateDraftFromConfigurationPage /> : <NoAccess />}
        />
        <Route
          path={`${pageRoutes.apiReports}/*`}
          element={
            hasAccessToReports ? (
              <Suspense fallback={<InlineLoader />}>
                <SmartViewApiReportPage />
              </Suspense>
            ) : (
              <NoAccess />
            )
          }
        />
        <Route
          path={`${pageRoutes.systemReports}/*`}
          element={
            hasAccessToReports ? (
              <Suspense fallback={<InlineLoader />}>
                <SmartViewSystemReportPage />
              </Suspense>
            ) : (
              <NoAccess />
            )
          }
        />
        <Route path={pageRoutes.standardReports} element={hasAccessToReports ? <Outlet /> : <NoAccess />}>
          <Route index element={<StandardReportsPage />} />
          <Route path={pageRoutes.balanceSheet} element={<BalanceSheetReport />} />
          <Route path={pageRoutes.incomeStatement} element={<IncomeStatementReport />} />
          <Route path={pageRoutes.apAgingSummary} element={<ApAgingSummaryReportPage />} />
          <Route path={pageRoutes.arAgingSummary} element={<ArAgingSummaryReportPage />} />
          <Route path={pageRoutes.apAgingDetail} element={<ApAgingDetailReportPage />} />
          <Route path={pageRoutes.arAgingDetail} element={<ArAgingDetailReportPage />} />
        </Route>
        <Route
          path={`${pageRoutes.reportTemplates}/*`}
          element={
            hasAccessToReports ? (
              <Suspense fallback={<InlineLoader />}>
                <SmartViewTemplatePage />
              </Suspense>
            ) : (
              <NoAccess />
            )
          }
        />
        <Route
          path={pageRoutes.workbookRunner}
          element={hasAccessToReports ? <WorkbookRunnerContainer /> : <NoAccess />}
        >
          <Route index element={<WorkbookRunnerPage />} />
          <Route path="template/:templateId" element={<Outlet />}>
            <Route index element={<WorkbookTemplateDetailsPage />} />
          </Route>
          <Route path="run/:runId" element={<Outlet />}>
            <Route index element={<WorkbookRunDetailsPage />} />
          </Route>
        </Route>

        <Route
          path={pageRoutes.organizationReports}
          element={hasAccessToOrganizationReports ? <UiOrganizationReportsPage /> : <NoAccess />}
        >
          <Route
            index
            element={<OrganizationReportsPage gridKey="ORGANIZATION_UI_REPORTS_GRID" pageTitle="Reports" />}
          />
        </Route>
        <Route
          path={pageRoutes.organizationApiReports}
          element={hasAccessToOrganizationReports ? <ApiOrganizationReportsPage /> : <NoAccess />}
        >
          <Route
            index
            element={<OrganizationReportsPage gridKey="ORGANIZATION_API_REPORTS_GRID" pageTitle="API Reports" />}
          />
        </Route>
        <Route path={pageRoutes.reportTemplates} element={<OrganizationTemplatesPage />}>
          <Route index element={<OrganizationTemplatesContainer />} />
          <Route
            path={pageRoutes.reportLevels}
            element={
              <GroupsPage
                permissions={["ManageOrganizationReportTemplates"]}
                title="Report Template Groups"
                type="template"
              />
            }
          />
        </Route>
      </Route>

      <Route path={pageRoutes.settings}>
        <Route
          path={pageRoutes.settingsCompanyGeneral}
          element={hasAccessToCompanySettings ? <GeneralCompanySettingsPage /> : <NoAccess />}
        />
        <Route
          path={pageRoutes.settingsOrganizationContacts}
          element={hasAccessToOrganizationUsers ? <OrganizationContactsPage /> : <NoAccess />}
        />
        <Route
          path={pageRoutes.settingsMembers}
          element={hasAccessToOrganizationUsers ? <MembersSettingsPage /> : <NoAccess />}
        />
        <Route path={pageRoutes.settingsUserGroups} element={hasAccessToOrganizationUsers ? <Outlet /> : <NoAccess />}>
          <Route index element={<UserGroupsPage />} />
          <Route path="new" element={<NewUserGroupPage />} />
          <Route path=":id" element={<UserGroupDetailsPage />} />
        </Route>
        <Route
          path={pageRoutes.settingsIntegrations}
          element={hasAccessToIntegrationSettings ? <IntegrationsPage /> : <NoAccess />}
        />
        <Route path={pageRoutes.importData} element={hasAccessToImportData ? <Outlet /> : <NoAccess />}>
          <Route index element={<ImportDataPage />} />
          <Route path={pageRoutes.importCsvData} element={<ImportCsvDataPage />} />
          <Route path=":id" element={<Outlet />}>
            <Route path={pageRoutes.importDetails} element={<ImportDataDetailsPage />} />
            <Route path={pageRoutes.importReview} element={<ImportDataReviewPage />} />
          </Route>
        </Route>
        <Route
          path={pageRoutes.settingsActivityLog}
          element={hasAccessToActivityLog ? <ActivityLogPage /> : <NoAccess />}
        />
        {isPortalFeatureEnabled("CustomObjectsEnabled") && (
          <Route path={pageRoutes.records} element={hasAccessToCompanySettings ? <Outlet /> : <NoAccess />}>
            <Route index element={<RecordsPage />} />
            <Route path=":type/new" element={<NewObjectDetailsPage />} />
            <Route path=":type/:id" element={<GenericObjectDetailsPage />} />
          </Route>
        )}
        <Route path={pageRoutes.dataModel} element={hasAccessToOrganizationDataModel ? <Outlet /> : <NoAccess />}>
          <Route index element={<DataModelPage />} />
          <Route path={pageRoutes.orgObjects} element={<Outlet />}>
            <Route path=":objectType" element={<ObjectDefinitionWrapperPage />}>
              <Route index element={<ObjectDetailsPage />} />
              <Route path={`${pageRoutes.layout}/:configurationId`} element={<ObjectLayoutPage />} />
              {isPortalFeatureEnabled("ObjectChecklistsEnabled") && (
                <Route path={`${pageRoutes.checklists}`} element={<ChecklistsConfigurationPage />} />
              )}
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path={pageRoutes.integration} element={hasAccessToIntegrationSettings ? <Outlet /> : <NoAccess />}>
        <Route path={pageRoutes.apiGateway} element={<ApiGatewayPage />} />
        <Route path={pageRoutes.snowflake} element={<SnowflakeConnectorPage />} />
        <Route path={pageRoutes.salesforce} element={<SalesforceConnectorPage />} />
        <Route path={pageRoutes.dealcloud} element={<DealCloudConnectorPage />} />
        <Route path={pageRoutes.dataBackup} element={<DataBackupPage />} />
        <Route path={pageRoutes.entriliaMcp} element={<EntriliaMcpPage />} />
        <Route path={pageRoutes.passthrough} element={<PassthroughPage />} />
        <Route path={pageRoutes.agenticWorkflows} element={<Outlet />}>
          <Route index element={<AgenticWorkflowsPage />} />
          <Route path={pageRoutes.agentInboxes} element={hasAccessToAgentInboxes ? <Outlet /> : <NoAccess />}>
            <Route path=":id" element={<AgentInboxSettingsPage />} />
          </Route>
        </Route>
      </Route>

      <Route path={pageRoutes.files} element={hasAccessToCompanyFiles ? <CompanyFilesPage /> : <NoAccess />} />

      <Route path={pageRoutes.expenseManagement} element={hasAccessToExpenseManagement ? <Outlet /> : <NoAccess />}>
        <Route path={pageRoutes.invoices} element={<Outlet />}>
          <Route index element={<InvoicesPage />} />
          <Route path="new" element={<NewObjectDetailsPage objectType="Invoice" />} />
          <Route path=":id" element={<InvoiceDetailsPage backButtonPath=".." />} />
        </Route>
        <Route path={pageRoutes.vendors} element={<Outlet />}>
          <Route index element={<VendorsPage />} />
          <Route path="new" element={<NewObjectDetailsPage objectType="Vendor" entityTypeCode="Vendor" />} />
          <Route path=":id" element={<VendorDetailsPage />} />
        </Route>
      </Route>

      <Route path={pageRoutes.portfolio} element={hasAccessToPortfolioMonitoring ? <Outlet /> : <NoAccess />}>
        <Route path={pageRoutes.fundInvestments} element={<Outlet />}>
          <Route index element={<FundInvestmentsPage />} />
          <Route path=":id" element={<FundInvestmentDetailsPage />} />
        </Route>
        <Route path={pageRoutes.portfolioCompanies} element={<Outlet />}>
          <Route index element={<PortfolioCompaniesPage />} />
          <Route path=":id" element={<PortfolioCompanyDetailsPage />} />
        </Route>
        <Route path={pageRoutes.dataRequestSetup} element={<DataRequestSetupPage />} />
        <Route path={pageRoutes.dataRequestTemplates} element={<Outlet />}>
          <Route path=":templateId" element={<DataRequestTemplatePage />} />
        </Route>
        <Route path={pageRoutes.dataRequests} element={<Outlet />}>
          <Route index element={<DataRequestsPage />} />
          <Route path=":id" element={<DataRequestPage />} />
        </Route>
        <Route path={pageRoutes.dataSubmissions} element={<Outlet />}>
          <Route index element={<DataSubmissionsPage />} />
          <Route path=":id" element={<DataSubmissionPage />} />
        </Route>
        <Route path={pageRoutes.metrics} element={<MetricsPage />} />
        <Route path={pageRoutes.scenarios} element={<Outlet />}>
          <Route index element={<ScenariosPage />} />
          <Route path="new" element={<NewObjectDetailsPage objectType="PortfolioMonitoringScenario" />} />
          <Route path=":id" element={<ScenarioDetailsPage />} />
        </Route>
        <Route path={pageRoutes.portfolioEmails} element={<PortfolioEmailDeliveryLogPage />} />
        <Route path={pageRoutes.portfolioPortal}>
          <Route index element={<PortfolioPortalPage />} />
        </Route>
      </Route>

      <Route path={pageRoutes.workflows}>
        <Route path={pageRoutes.approvals} element={<Outlet />}>
          <Route index element={<ApprovalsPage />} />
          <Route path={`${pageRoutes.approvalObject}/:type/:id`} element={<ApprovalObjectPage />} />
          <Route
            path={`${pageRoutes.changeRequest}/:id`}
            element={
              <ChangeRequestDetailsPage
                backButtonPath={`/${clientCode}/${pageRoutes.workflows}/${pageRoutes.approvals}`}
              />
            }
          />
        </Route>
        <Route path={pageRoutes.workflowRuns} element={hasAccessToWorkflows ? <Outlet /> : <NoAccess />}>
          <Route index element={<WorkflowGroupsPage />} />
          <Route path=":templateId" element={<WorkflowRunsPage />} />
          <Route path=":templateId/:id" element={<WorkflowRunDetailsPage />} />
        </Route>
        <Route path={pageRoutes.workflowTemplates} element={hasAccessToWorkflows ? <Outlet /> : <NoAccess />}>
          <Route index element={<WorkflowTemplatesPage />} />
          <Route path=":versionId" element={<Outlet />}>
            <Route index element={<WorkflowTemplateDetailsPage />} />
            <Route path={`${pageRoutes.workflowActivity}/:activityId`} element={<ApprovalConfigurationPage />} />
            <Route
              path={`${pageRoutes.workflowActivity}/:activityId/${pageRoutes.fields}`}
              element={<WorkflowTemplateActivityFieldsPage />}
            />
          </Route>
        </Route>
        <Route path={pageRoutes.workflowChecklists} element={hasAccessToWorkflows ? <Outlet /> : <NoAccess />}>
          <Route path=":type/:id" element={<WorkflowChecklistDetailsPage />} />
        </Route>
        {isPortalFeatureEnabled("ObjectChecklistsEnabled") && (
          <Route path={pageRoutes.checklists} element={hasAccessToOrganizationDataModel ? <Outlet /> : <NoAccess />}>
            <Route index element={<ChecklistsPage />} />
            <Route path={pageRoutes.taskTemplates} element={<Outlet />}>
              <Route path="new" element={<NewChecklistTaskTemplatePage />} />
              <Route path=":id" element={<TaskTemplateDetailsPage />} />
            </Route>
          </Route>
        )}
      </Route>

      <Route path={pageRoutes.videoLibrary} element={<Outlet />}>
        <Route index element={<VideoLibraryPage />} />
        <Route path=":id" element={<VideoPage />} />
      </Route>

      <Route path={pageRoutes.aiDashboards} element={hasAccessToAiDashboards ? <Outlet /> : <NoAccess />}>
        <Route index element={<ArtifactsListPage />} />
        <Route path=":id">
          <Route index element={<Navigate to="view" replace />} />
          <Route path="view" element={<ArtifactPage key="view" mode="view" />}>
            {artifactReportRoutes}
          </Route>
          <Route path="edit" element={<ArtifactPage key="edit" mode="edit" />}>
            {artifactReportRoutes}
          </Route>
        </Route>
      </Route>

      <Route path={pageRoutes.dashboards} element={hasAccessToDashboards ? <Outlet /> : <NoAccess />}>
        <Route index element={<CompanyDashboardsPage />} />
        <Route path=":id" element={<DashboardPage />}>
          <Route path="view" element={<DashboardPageView />}>
            <Route index element={null} />
            <Route path="widgets/:widgetId" element={<TileViewOverlay />} />
          </Route>
          <Route path="edit" element={<DashboardPageEdit />}>
            <Route index element={null} />
            <Route path="widgets/:widgetId" element={<TileViewOverlay />} />
            <Route path="widgets/:widgetId/edit" element={<SmartViewWidgetReportEditorApp />}>
              <Route index element={<SmartViewWidgetReportEditor />} />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path={pageRoutes.accountSettings} element={<AccountSettingsPage />} />

      <Route path={pageRoutes.bcRedirect} element={<BusinessCentralRedirectPage />} />

      <Route
        path={pageRoutes.fundOpsTransactionLink}
        element={hasAccessToFundOpsLink ? <FundOpsTransactionLinkPage /> : <NoAccess />}
      />

      <Route path="*" element={<Navigate replace to={`/${client}/${pageRoutes.home}`} />} />
    </Routes>
  );
};

export default Pages;
